import {showNotification} from "@/actions";
import {defineMessage, MessageDescriptor} from "react-intl";
import {ApolloClient, useApolloClient} from "@apollo/client";
import * as React from "react";
import {useEffect, useState} from "react";
import {areObjectsEqual, objectKeys} from "@/tools";
import {AddressBookEntryData} from "@/api";
import {
    AddAddressBookEntryMutation,
    AddAddressBookEntryMutationVariables,
    UpdateAddressBookEntryMutation,
    UpdateAddressBookEntryMutationVariables,
} from "./queries.generated";
import {MutationFunction} from "@/utils/Form";
import {useAppDispatch} from "@/hooks";

export type FieldError = {
    type: string;
    context: {[s: string]: any};
};

type FormErrors = {[s in keyof Partial<AddressBookEntryData>]: FieldError};

export type SubmitError = {
    type: string;
    location: string[];
    context: string;
};

type SubmitResponse = {
    notification?: ReturnType<typeof showNotification>;
    errors?: SubmitError[];
};

type SubmitResponseType = Promise<SubmitResponse>;

export interface FormHandler {
    title: MessageDescriptor;
    initialEntry: AddressBookEntryData;
    mutation: MutationFunction<any, any>;
    // addMutation: MutationFunction<AddAddressBookEntryMutation, AddAddressBookEntryMutationVariables>;
    // updateMutation: MutationFunction<UpdateAddressBookEntryMutation, UpdateAddressBookEntryMutationVariables>;

    submit(target: string, data: AddressBookEntryData): SubmitResponseType;
}

const defaultEntry: AddressBookEntryData = {
    sn: "",
    givenName: "",
    telephoneNumber: [],
    mail: "",
    departmentNumber: "",
    businessCategory: "",
    description: "",
    mobile: "",
    homePhone: "",
    o: "",
};

export class CreateFormHandler implements FormHandler {
    title = defineMessage({id: "address_book.create", defaultMessage: "Create new entry"});
    initialEntry = defaultEntry;
    mutation: MutationFunction<AddAddressBookEntryMutation, AddAddressBookEntryMutationVariables>;

    constructor(mutation: MutationFunction<AddAddressBookEntryMutation, AddAddressBookEntryMutationVariables>) {
        this.mutation = mutation;
    }

    async submit(target: string, data: AddressBookEntryData): SubmitResponseType {
        const {data: result} = await this.mutation({
            variables: {
                target,
                entry: data,
            },
        });

        if (result.addAddressBookEntry.errors != null)
            return {
                notification: showNotification(
                    defineMessage({id: "validation_error", defaultMessage: "Validation error!"}),
                    "error",
                ),
                errors: result.addAddressBookEntry.errors as SubmitError[],
            };

        return {
            notification: showNotification(
                defineMessage({id: "address_book.created", defaultMessage: "New entry created"}),
                "success",
            ),
        };
    }
}

export class UpdateFormHandler implements FormHandler {
    title = defineMessage({id: "address_book.update", defaultMessage: "Update entry"});
    initialEntry: AddressBookEntryData;
    private readonly cn: string;
    mutation: MutationFunction<UpdateAddressBookEntryMutation, UpdateAddressBookEntryMutationVariables>;

    constructor(
        cn: string,
        entry: AddressBookEntryData,
        mutation: MutationFunction<UpdateAddressBookEntryMutation, UpdateAddressBookEntryMutationVariables>,
    ) {
        this.cn = cn;
        this.initialEntry = objectKeys(defaultEntry).reduce((target, key: keyof typeof defaultEntry) => {
            (target[key] as any) = entry[key] === null ? "" : entry[key];
            return target;
        }, {} as AddressBookEntryData);
        this.mutation = mutation;
    }

    async submit(target: string, data: AddressBookEntryData): SubmitResponseType {
        const {data: result} = await this.mutation({
            variables: {
                target,
                entry: data,
                cn: this.cn,
            },
        });

        if (result.updateAddressBookEntry.errors != null)
            return {
                errors: result.updateAddressBookEntry.errors as SubmitError[],
            };

        this.initialEntry = data;
        return {
            notification: showNotification(
                defineMessage({id: "address_book.updated", defaultMessage: "Entry updated"}),
                "success",
            ),
        };
    }
}

export class NullHandler implements FormHandler {
    title = defineMessage({id: "address_book.create", defaultMessage: "Create new entry"});
    initialEntry = defaultEntry;
    mutation: undefined;

    async submit(__: string, ___: AddressBookEntryData): SubmitResponseType {
        return {
            notification: showNotification(
                defineMessage({id: "address_book.created", defaultMessage: "New entry created"}),
                "success",
            ),
        };
    }
}

export class Form {
    readonly data: AddressBookEntryData;

    readonly client: ApolloClient<object>;
    readonly setData: (entry: AddressBookEntryData) => void;
    readonly dispatch: ReturnType<typeof useAppDispatch>;
    readonly target: string;
    readonly handler: FormHandler;
    readonly errors: FormErrors;
    readonly setErrors: (e: FormErrors) => void;

    constructor(target: string, handler: FormHandler) {
        this.handler = handler;
        this.client = useApolloClient();
        [this.data, this.setData] = useState<AddressBookEntryData>(defaultEntry);
        this.dispatch = useAppDispatch();
        [this.errors, this.setErrors] = useState<FormErrors>({});
        this.target = target;
        const initialEntry = this.handler.initialEntry;

        useEffect(() => {
            this.setData(initialEntry);
        }, [initialEntry]);
    }

    get isChanged(): boolean {
        return !areObjectsEqual(this.handler.initialEntry, this.data);
    }

    createDataUpdater(name: keyof AddressBookEntryData) {
        return (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            this.setData({...this.data, [name]: event.target.value});
        };
    }

    hasError(name: keyof AddressBookEntryData): boolean {
        return !!this.errors[name];
    }

    reset() {
        this.setData(this.handler.initialEntry);
        this.setErrors({});
    }

    async submit(): Promise<boolean> {
        const response = await this.handler.submit(this.target, this.data);
        if (response.notification) this.dispatch(response.notification);
        this.processErrors(response.errors || []);
        return !response.errors;
    }

    private processErrors(errors: SubmitError[]) {
        const formErrors: FormErrors = {};
        for (const err of errors) {
            const key = err.location[1] as keyof AddressBookEntryData;
            formErrors[key] = {
                type: err.type,
                context: err.context ? JSON.parse(err.context) : {},
            };
        }
        this.setErrors(formErrors);
    }
}
